import React, { useEffect, memo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CalendarIcon, UserIcon, ArrowLeftIcon, TagIcon, ShareIcon, FacebookIcon, TwitterIcon, LinkedinIcon } from 'lucide-react';
// This would typically come from an API or CMS
const blogPosts = [{
  id: 1,
  title: 'The Healing Power of Lavender Essential Oil',
  excerpt: 'Discover the many therapeutic benefits of lavender oil and how to incorporate it into your daily wellness routine.',
  content: `
      <p>Lavender essential oil is one of the most versatile and beloved oils in aromatherapy, prized for its calming scent and numerous therapeutic properties. Derived from the flowers of the lavender plant (Lavandula angustifolia), this essential oil has been used for centuries to promote relaxation, ease anxiety, and support overall wellbeing.</p>
      <h2>The History of Lavender</h2>
      <p>The use of lavender dates back thousands of years, with evidence of its use by ancient civilizations including the Egyptians, Romans, and Greeks. The name "lavender" comes from the Latin word "lavare," meaning "to wash," as it was commonly used in baths for its cleansing properties and pleasant fragrance.</p>
      <p>Throughout history, lavender has been used medicinally to treat everything from insomnia and anxiety to headaches and skin conditions. It was also used as a natural perfume and to freshen linens and clothing.</p>
      <h2>Therapeutic Benefits of Lavender Essential Oil</h2>
      <h3>1. Promotes Relaxation and Sleep</h3>
      <p>Perhaps the most well-known benefit of lavender oil is its ability to promote relaxation and improve sleep quality. Research has shown that inhaling lavender can help reduce anxiety and stress, making it easier to fall asleep and stay asleep. Try adding a few drops to a diffuser before bedtime or spraying a lavender mist on your pillow.</p>
      <h3>2. Relieves Headaches</h3>
      <p>Lavender oil can be effective in reducing the severity and frequency of headaches, particularly tension headaches and migraines. Gently massage diluted lavender oil onto your temples and forehead, or inhale the scent directly from the bottle for relief.</p>
      <h3>3. Soothes Skin Irritations</h3>
      <p>Lavender oil has natural anti-inflammatory and antiseptic properties, making it beneficial for various skin conditions. It can help soothe and heal minor burns, insect bites, eczema, and acne. Always dilute with a carrier oil before applying to the skin.</p>
      <h3>4. Eases Muscle Tension</h3>
      <p>When added to a warm bath or massage oil, lavender can help relieve muscle tension and soreness. Its anti-inflammatory properties can reduce swelling and pain in muscles and joints.</p>
      <h3>5. Natural Insect Repellent</h3>
      <p>The scent of lavender is pleasant to humans but repulsive to many insects, including mosquitoes, flies, and moths. Use lavender oil in a spray or diffuser to keep bugs away naturally.</p>
      <h2>How to Use Lavender Essential Oil</h2>
      <h3>Aromatherapy</h3>
      <p>Add 5-7 drops to a diffuser filled with water and enjoy the calming scent throughout your space.</p>
      <h3>Topical Application</h3>
      <p>Mix 3-5 drops with a tablespoon of carrier oil (such as jojoba or sweet almond) and apply to skin as needed. This is great for massage or treating skin conditions.</p>
      <h3>Bath</h3>
      <p>Add 8-10 drops to a warm bath along with a cup of Epsom salts for a relaxing soak that eases muscle tension and promotes sleep.</p>
      <h3>Linen Spray</h3>
      <p>Mix 10-15 drops with water in a spray bottle and use to freshen linens, clothing, or room air.</p>
      <h2>Quality Matters</h2>
      <p>When purchasing lavender essential oil, it's important to choose a high-quality, pure oil for maximum therapeutic benefits. At Shiriaka Essentials, our lavender oil is sourced from organic lavender grown in Kenya's highlands, steam-distilled to preserve its natural properties, and rigorously tested for purity.</p>
      <p>Experience the healing power of lavender essential oil and discover why it has remained a staple in natural wellness for thousands of years.</p>
    `,
  image: 'https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?q=80&w=1080&auto=format&fit=crop',
  date: 'May 15, 2023',
  author: 'Sarah Kimani',
  category: 'Wellness',
  tags: ['Lavender', 'Sleep', 'Relaxation', 'Skincare'],
  slug: 'healing-power-of-lavender'
}, {
  id: 2,
  title: 'Essential Oils for Better Sleep: A Natural Approach',
  excerpt: 'Learn how to create the perfect bedtime routine with essential oils that promote relaxation and deep sleep.',
  content: `
      <p>In our fast-paced world, quality sleep has become increasingly elusive. The good news is that essential oils can offer a natural solution to sleep problems, helping you create a bedtime routine that promotes relaxation and prepares your body and mind for restful sleep.</p>
      <h2>The Importance of Quality Sleep</h2>
      <p>Sleep is essential for overall health and wellbeing. During sleep, your body repairs tissues, processes information, and consolidates memories. Chronic sleep deprivation can lead to serious health issues, including weakened immunity, increased risk of heart disease, impaired cognitive function, and mood disorders.</p>
      <h2>How Essential Oils Can Help</h2>
      <p>Essential oils work through two primary pathways to improve sleep: inhalation and topical application. When inhaled, the aromatic molecules in essential oils stimulate the olfactory system, which has direct connections to the brain's limbic system—the area responsible for emotions and memory. This can trigger relaxation responses, reducing stress and anxiety that often interfere with sleep.</p>
      <p>When applied topically (properly diluted with a carrier oil), essential oils can be absorbed through the skin, providing localized benefits and entering the bloodstream to exert their therapeutic effects throughout the body.</p>
      <h2>Top Essential Oils for Sleep</h2>
      <h3>1. Lavender (Lavandula angustifolia)</h3>
      <p>The undisputed champion of sleep-promoting essential oils, lavender has been extensively studied for its sedative and anxiety-reducing properties. Research has shown that lavender can increase slow-wave sleep (deep sleep), improve sleep quality, and help people fall asleep faster.</p>
      <h3>2. Chamomile (Matricaria chamomilla)</h3>
      <p>Known for its gentle, calming properties, chamomile essential oil can help reduce anxiety and promote relaxation. Its mild, apple-like scent makes it particularly suitable for those who find lavender too strong.</p>
      <h3>3. Bergamot (Citrus bergamia)</h3>
      <p>Unlike most citrus oils which are energizing, bergamot has unique calming properties. It helps reduce stress and anxiety while gently lifting mood, making it ideal for those whose sleep is disrupted by racing thoughts or worry.</p>
      <h3>4. Ylang Ylang (Cananga odorata)</h3>
      <p>With its exotic, floral scent, ylang ylang helps lower blood pressure and heart rate, creating the physiological conditions conducive to sleep. It's particularly effective for those whose sleep problems stem from physical tension.</p>
      <h3>5. Cedarwood (Juniperus virginiana)</h3>
      <p>The warm, woody aroma of cedarwood has a grounding effect that helps calm an overactive mind. It also stimulates the production of serotonin, which converts to melatonin—the hormone that regulates sleep cycles.</p>
      <h2>Creating Your Perfect Bedtime Routine</h2>
      <h3>1. Begin with a Relaxing Bath</h3>
      <p>Add 5-7 drops of lavender or chamomile essential oil to a warm bath, along with 1 cup of Epsom salts. Soak for 20 minutes to relax muscles and calm your mind.</p>
      <h3>2. Diffuse Sleep-Promoting Oils</h3>
      <p>About 30 minutes before bedtime, start diffusing a sleep blend in your bedroom. Try this recipe:</p>
      <ul>
        <li>3 drops Lavender</li>
        <li>2 drops Bergamot</li>
        <li>1 drop Cedarwood</li>
      </ul>
      <h3>3. Apply a Topical Sleep Blend</h3>
      <p>Mix the following with 1 tablespoon of carrier oil (such as jojoba or sweet almond):</p>
      <ul>
        <li>4 drops Lavender</li>
        <li>2 drops Ylang Ylang</li>
        <li>2 drops Chamomile</li>
      </ul>
      <p>Massage this blend onto your chest, neck, and behind your ears before getting into bed.</p>
      <h3>4. Create a Pillow Spray</h3>
      <p>In a small spray bottle, combine:</p>
      <ul>
        <li>1/4 cup distilled water</li>
        <li>1 tablespoon witch hazel</li>
        <li>10 drops Lavender</li>
        <li>5 drops Bergamot</li>
      </ul>
      <p>Shake well and lightly mist your pillow and bedding before sleep.</p>
      <h2>Additional Sleep Tips</h2>
      <ul>
        <li>Maintain a consistent sleep schedule, going to bed and waking up at the same time each day.</li>
        <li>Create a dark, cool, and quiet sleeping environment.</li>
        <li>Avoid screens (phones, tablets, computers) for at least an hour before bedtime.</li>
        <li>Limit caffeine and alcohol, especially in the afternoon and evening.</li>
        <li>Engage in regular physical activity, but not too close to bedtime.</li>
      </ul>
      <p>By incorporating essential oils into a thoughtful bedtime routine, you can create the perfect conditions for restorative sleep. Sweet dreams!</p>
    `,
  image: 'https://images.unsplash.com/photo-1505992525839-8040fba23095?q=80&w=1080&auto=format&fit=crop',
  date: 'June 2, 2023',
  author: 'David Mwangi',
  category: 'Lifestyle',
  tags: ['Sleep', 'Relaxation', 'Wellness', 'Nighttime Routine'],
  slug: 'essential-oils-better-sleep'
}, {
  id: 3,
  title: 'Sourcing Essential Oils: Our Journey Through Kenya',
  excerpt: 'Follow our team as we explore the rich botanical landscapes of Kenya to source the finest ingredients for our oils.',
  content: `<p>This is the full content of the blog post about sourcing essential oils in Kenya...</p>`,
  image: 'https://images.unsplash.com/photo-1580757468214-c73f7062a5cb?q=80&w=1080&auto=format&fit=crop',
  date: 'June 28, 2023',
  author: 'James Omondi',
  category: 'Behind the Scenes',
  tags: ['Sourcing', 'Kenya', 'Sustainability', 'Farming'],
  slug: 'sourcing-essential-oils-kenya'
}, {
  id: 4,
  title: 'DIY Essential Oil Blends for Your Home Diffuser',
  excerpt: 'Create your own signature scents with these easy-to-follow recipes for your home diffuser.',
  content: `<p>This is the full content of the blog post about DIY essential oil blends...</p>`,
  image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=1080&auto=format&fit=crop',
  date: 'July 10, 2023',
  author: 'Grace Wanjiku',
  category: 'DIY',
  tags: ['DIY', 'Blends', 'Diffuser', 'Recipes'],
  slug: 'diy-essential-oil-blends'
}, {
  id: 5,
  title: 'Essential Oils for Stress Relief and Anxiety',
  excerpt: 'Discover which essential oils can help reduce stress and anxiety, and the best ways to use them.',
  content: `<p>This is the full content of the blog post about stress relief...</p>`,
  image: 'https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1080&auto=format&fit=crop',
  date: 'August 5, 2023',
  author: 'Esther Njeri',
  category: 'Wellness',
  tags: ['Stress Relief', 'Anxiety', 'Mental Health', 'Relaxation'],
  slug: 'essential-oils-stress-relief'
}, {
  id: 6,
  title: 'The Science Behind Essential Oils: How They Work',
  excerpt: 'A deep dive into the chemistry of essential oils and how they affect our bodies and minds.',
  content: `<p>This is the full content of the blog post about the science of essential oils...</p>`,
  image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?q=80&w=1080&auto=format&fit=crop',
  date: 'September 12, 2023',
  author: 'Dr. Peter Kamau',
  category: 'Education',
  tags: ['Science', 'Chemistry', 'Research', 'Aromatherapy'],
  slug: 'science-behind-essential-oils'
}];
// Related posts function
const getRelatedPosts = (currentPost, allPosts, count = 3) => {
  return allPosts.filter(post => post.id !== currentPost.id && post.category === currentPost.category).slice(0, count);
};
const BlogPost = () => {
  const {
    slug
  } = useParams();
  const navigate = useNavigate();
  // Find the current post
  const currentPost = blogPosts.find(post => post.slug === slug);
  // If post not found, redirect to blog
  useEffect(() => {
    if (!currentPost) {
      navigate('/blog');
    }
  }, [currentPost, navigate]);
  if (!currentPost) {
    return null;
  }
  // Get related posts
  const relatedPosts = getRelatedPosts(currentPost, blogPosts);
  return <div className="bg-[#f8f9f4] min-h-screen w-full" data-id="element-657">
      {/* Hero Section */}
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden" data-id="element-658">
        <img src={currentPost.image} alt={currentPost.title} className="w-full h-full object-cover" onError={e => {
        e.currentTarget.src = 'https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?q=80&w=1080&auto=format&fit=crop';
      }} data-id="element-659" />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end" data-id="element-660">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-661">
            <button onClick={() => navigate('/blog')} className="mb-6 inline-flex items-center text-white hover:text-green-100 transition-colors" data-id="element-662">
              <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-663" />
              Back to Blog
            </button>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl" data-id="element-664">
              {currentPost.title}
            </h1>
            <div className="flex flex-wrap items-center text-white text-sm mb-4" data-id="element-665">
              <CalendarIcon className="h-4 w-4 mr-2" data-id="element-666" />
              <span data-id="element-667">{currentPost.date}</span>
              <span className="mx-3" data-id="element-668">•</span>
              <UserIcon className="h-4 w-4 mr-2" data-id="element-669" />
              <span data-id="element-670">{currentPost.author}</span>
              <span className="mx-3" data-id="element-671">•</span>
              <TagIcon className="h-4 w-4 mr-2" data-id="element-672" />
              <span data-id="element-673">{currentPost.category}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-674">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-675">
          {/* Main content */}
          <div className="lg:col-span-2" data-id="element-676">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-10" data-id="element-677">
              <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-600 prose-a:text-[#2e7d32] prose-a:font-medium" dangerouslySetInnerHTML={{
              __html: currentPost.content
            }} data-id="element-678" />
              {/* Tags */}
              <div className="mt-10 pt-6 border-t border-gray-200" data-id="element-679">
                <div className="flex flex-wrap items-center gap-2" data-id="element-680">
                  <span className="text-gray-700 font-medium" data-id="element-681">Tags:</span>
                  {currentPost.tags.map(tag => <Link key={tag} to={`/blog?tag=${tag.toLowerCase()}`} className="px-3 py-1 bg-[#f0f7eb] text-[#2e7d32] text-sm rounded-full hover:bg-[#e8f5e9] transition-colors" data-id="element-682">
                      {tag}
                    </Link>)}
                </div>
              </div>
              {/* Share */}
              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-683">
                <div className="flex items-center" data-id="element-684">
                  <span className="text-gray-700 font-medium mr-4" data-id="element-685">Share:</span>
                  <div className="flex space-x-3" data-id="element-686">
                    <a href="#" className="text-gray-500 hover:text-[#2e7d32] transition-colors" data-id="element-687">
                      <FacebookIcon className="h-5 w-5" data-id="element-688" />
                    </a>
                    <a href="#" className="text-gray-500 hover:text-[#2e7d32] transition-colors" data-id="element-689">
                      <TwitterIcon className="h-5 w-5" data-id="element-690" />
                    </a>
                    <a href="#" className="text-gray-500 hover:text-[#2e7d32] transition-colors" data-id="element-691">
                      <LinkedinIcon className="h-5 w-5" data-id="element-692" />
                    </a>
                    <a href="#" className="text-gray-500 hover:text-[#2e7d32] transition-colors" data-id="element-693">
                      <ShareIcon className="h-5 w-5" data-id="element-694" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Author bio */}
            <div className="bg-white rounded-xl shadow-md p-6 mt-8" data-id="element-695">
              <div className="flex items-center" data-id="element-696">
                <img src={`https://ui-avatars.com/api/?name=${currentPost.author.replace(' ', '+')}&background=e8f5e9&color=2e7d32`} alt={currentPost.author} className="w-16 h-16 rounded-full mr-4" data-id="element-697" />
                <div data-id="element-698">
                  <h3 className="font-bold text-gray-900" data-id="element-699">
                    {currentPost.author}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-700">
                    Expert in essential oils and natural wellness with over 10
                    years of experience in aromatherapy.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-701">
            {/* Related posts */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-702">
              <h3 className="font-bold text-gray-900 mb-4 text-lg" data-id="element-703">
                Related Posts
              </h3>
              <div className="space-y-6" data-id="element-704">
                {relatedPosts.map(post => <Link key={post.id} to={`/blog/${post.slug}`} className="flex group" data-id="element-705">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0" data-id="element-706">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?q=80&w=150&auto=format&fit=crop';
                  }} data-id="element-707" />
                    </div>
                    <div className="ml-4" data-id="element-708">
                      <h4 className="font-medium text-gray-900 group-hover:text-[#2e7d32] transition-colors line-clamp-2" data-id="element-709">
                        {post.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-710">{post.date}</p>
                    </div>
                  </Link>)}
              </div>
            </div>
            {/* Categories */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-711">
              <h3 className="font-bold text-gray-900 mb-4 text-lg" data-id="element-712">
                Categories
              </h3>
              <div className="space-y-2" data-id="element-713">
                {['Wellness', 'Lifestyle', 'DIY', 'Education', 'Behind the Scenes'].map(category => <Link key={category} to={`/blog?category=${category}`} className="flex items-center justify-between py-2 border-b border-gray-100 group" data-id="element-714">
                    <span className="text-gray-600 group-hover:text-[#2e7d32] transition-colors" data-id="element-715">
                      {category}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-716">
                      {blogPosts.filter(post => post.category === category).length}
                    </span>
                  </Link>)}
              </div>
            </div>
            {/* Newsletter */}
            <div className="bg-[#f0f7eb] rounded-xl shadow-md p-6" data-id="element-717">
              <h3 className="font-bold text-gray-900 mb-2 text-lg" data-id="element-718">
                Subscribe to Our Newsletter
              </h3>
              <p className="text-gray-600 text-sm mb-4" data-id="element-719">
                Get the latest articles, tips, and offers straight to your
                inbox.
              </p>
              <form className="space-y-3" data-id="element-720">
                <input type="email" placeholder="Your email address" className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-[#2e7d32] focus:border-[#2e7d32] outline-none" data-id="element-721" />
                <button type="submit" className="w-full bg-[#2e7d32] text-white py-2 rounded-lg hover:bg-[#1b5e20] transition-colors" data-id="element-722">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default BlogPost;